import { useEffect, useMemo, useState } from 'react';
import type { Allergen, DietaryConcern, DietaryConcernReason, DietaryConcernSeverity, DietaryConcernTag, Dish } from '../../shared/menu';
import { priceForDisplay } from '../../shared/currencies';
import { AllergenChip } from './AllergenChip';
import { ConfirmDialog } from './ConfirmDialog';
import { DishDetailsPanel } from './DishDetailsPanel';
import { ReasonPicker } from './ReasonPicker';
import { useT, type StringKey } from '../lib/i18n';
import { DIETARY_TAG_LABELS, TAG_TO_CONFLICTING_ALLERGENS } from '../lib/dietaryPhrasebook';
import { quickModFor, type QuickModKind } from '../lib/quickModPhrasebook';
import { useSettings } from '../lib/store';
import { DISH_LOW_CONFIDENCE_THRESHOLD } from '../../shared/menu';

interface DishSheetProps {
  dish: Dish;
  currency: string | null;
  // Menu's detected source language — drives the language of the modifications
  // we insert (so the waiter's hand-off screen is readable in their language).
  sourceLanguage: string;
  // Menu id — needed for the "What is this dish?" detail panel to
  // address the explain endpoint.
  menuId?: string;
  // Current cart's meal mode (kosher meat+dairy separation rule). Null when
  // user isn't kosher OR cart is empty/pareve-only. When set, adding a dish
  // of the opposite kosherCategory fires the meal-mode contradiction guard.
  existingMealMode?: 'meat' | 'dairy' | null;
  initialQuantity: number;
  initialModifications: string;
  initialDietaryTags?: DietaryConcern[];
  initialOverridden?: boolean;
  onConfirm: (args: {
    quantity: number;
    modifications: string;
    dietaryTags: DietaryConcern[];
    overridden: boolean;
  }) => void;
  onClose: () => void;
}

const TAG_ORDER: DietaryConcernTag[] = [
  'vegan',
  'vegetarian',
  'gluten_free',
  'no_nuts',
  'no_dairy',
  'no_pork',
  'no_shellfish',
  'no_alcohol',
  'no_fish_sauce',
  'no_pork_lard',
  'no_cross_contamination',
];

// Display names used in the safety strip ("Contains: peanuts, milk").
// Mirrors the AllergenChip name map but kept inline to avoid a circular
// import — these are user-facing safety labels, not chip aria-labels.
const ALLERGEN_NAME: Record<Allergen, string> = {
  nuts: 'tree nuts',
  peanut: 'peanuts',
  shellfish: 'shellfish',
  fish: 'fish',
  egg: 'egg',
  soy: 'soy',
  wheat: 'gluten',
  sesame: 'sesame',
  dairy: 'dairy',
  pork: 'pork',
  alcohol: 'alcohol',
};

// Tap once → preference; tap again → strict; tap again → off.
function nextSeverity(current: DietaryConcernSeverity | null): DietaryConcernSeverity | null {
  if (current === null) return 'preference';
  if (current === 'preference') return 'strict';
  return null;
}

export function DishSheet({
  dish,
  currency,
  sourceLanguage,
  menuId,
  existingMealMode,
  initialQuantity,
  initialModifications,
  initialDietaryTags,
  initialOverridden,
  onConfirm,
  onClose,
}: DishSheetProps) {
  const t = useT();
  const dietaryReason = useSettings((s) => s.dietaryReason);
  const targetLanguage = useSettings((s) => s.targetLanguage);
  const setDietaryReason = useSettings((s) => s.setDietaryReason);

  const [quantity, setQuantity] = useState(Math.max(1, initialQuantity));
  const [mods, setMods] = useState(initialModifications);
  const [tagSeverity, setTagSeverity] = useState<Map<DietaryConcernTag, DietaryConcernSeverity>>(() => {
    const m = new Map<DietaryConcernTag, DietaryConcernSeverity>();
    for (const c of initialDietaryTags ?? []) m.set(c.tag, c.severity);
    return m;
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  // "Adjust for this dish" defaults open if the user already has edits on
  // this line — they came back to MODIFY. Otherwise collapsed; the active-
  // state line above the CTA still shows what's set so they're never blind.
  const hasInitialEdits =
    initialQuantity > 1 ||
    (initialModifications?.trim().length ?? 0) > 0 ||
    (initialDietaryTags?.length ?? 0) > 0;
  const [adjustOpen, setAdjustOpen] = useState(hasInitialEdits);

  const [contradictionPending, setContradictionPending] = useState<{ tag: DietaryConcernTag; allergen: string } | null>(null);
  const [mealModePending, setMealModePending] = useState<{ existing: 'meat' | 'dairy'; incoming: 'meat' | 'dairy' } | null>(null);
  const [reasonPickerOpen, setReasonPickerOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Body scroll lock while the sheet is up — otherwise the menu behind
  // scrolls when the user drags outside the sheet panel on mobile.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const appendChip = (s: string) => {
    setMods((cur) => (cur ? `${cur}, ${s}` : s));
  };

  const cycleTag = (tag: DietaryConcernTag) => {
    const newSev = nextSeverity(tagSeverity.get(tag) ?? null);
    setTagSeverity((cur) => {
      const next = new Map(cur);
      if (newSev === null) next.delete(tag);
      else next.set(tag, newSev);
      return next;
    });
    if (newSev === 'strict' && dietaryReason === null) {
      setReasonPickerOpen(true);
    }
  };

  // Pre-emptive conflict detection — surface a banner BEFORE the user taps
  // Add, not just at submit time. Fires when any strict per-dish tag matches
  // a confirmed allergen on this dish.
  const activeConflict = useMemo((): { tag: DietaryConcernTag; allergen: string } | null => {
    for (const [tag, sev] of tagSeverity) {
      if (sev !== 'strict') continue;
      const conflicts = TAG_TO_CONFLICTING_ALLERGENS[tag];
      if (!conflicts) continue;
      const hit = dish.allergens.find((a) => conflicts.includes(a));
      if (hit) return { tag, allergen: hit };
    }
    return null;
  }, [dish.allergens, tagSeverity]);

  const buildConcerns = (): DietaryConcern[] => {
    const out: DietaryConcern[] = [];
    for (const [tag, severity] of tagSeverity) {
      const concern: DietaryConcern = { tag, severity };
      if (severity === 'strict' && dietaryReason) concern.reason = dietaryReason;
      out.push(concern);
    }
    return out;
  };

  const submit = (overridden: boolean) => {
    onConfirm({ quantity, modifications: mods, dietaryTags: buildConcerns(), overridden });
  };

  const handleAdd = () => {
    if (activeConflict) {
      setContradictionPending(activeConflict);
      return;
    }
    const incoming = dish.kosherCategory;
    if (existingMealMode && (incoming === 'meat' || incoming === 'dairy') && incoming !== existingMealMode) {
      setMealModePending({ existing: existingMealMode, incoming });
      return;
    }
    submit(initialOverridden ?? false);
  };

  const unitLabel = priceForDisplay({ price: dish.price, priceText: dish.priceText, currency, preferOriginal: false });
  const noPriceShown = dish.price === null && !dish.priceText;
  const isLowConfidence = dish.confidence < DISH_LOW_CONFIDENCE_THRESHOLD;
  // CTA shows line total when qty > 1 so the user sees what they're
  // committing to. priceText (non-numeric like "Mkt") can't multiply; show
  // it as-is with a trailing "× qty" for clarity.
  const ctaPriceLabel = (() => {
    if (dish.price !== null) {
      return priceForDisplay({ price: dish.price * quantity, priceText: null, currency, preferOriginal: false });
    }
    if (dish.priceText) {
      return quantity > 1 ? `${dish.priceText} × ${quantity}` : dish.priceText;
    }
    return '';
  })();

  // Active state line above the CTA — surfaces what the user has set inside
  // the collapsed "Adjust" disclosure so it isn't a black box. Quantity is
  // visible separately in the footer, so it's not included here.
  const activeBits: string[] = [];
  for (const [tag, sev] of tagSeverity) {
    if (sev !== 'strict') continue;
    activeBits.push(`${t(`dish.tag.${tag}` as StringKey)} (${t('dish.severityStrict').toLowerCase()})`);
  }
  for (const [tag, sev] of tagSeverity) {
    if (sev !== 'preference') continue;
    activeBits.push(t(`dish.tag.${tag}` as StringKey));
  }
  const activeState = activeBits.join(' · ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={dish.nameTranslated}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg)',
      }}
    >
      {/* Close header — X top-right, thin border-bottom when content scrolls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: 'max(8px, env(safe-area-inset-top)) 12px 8px',
          flexShrink: 0,
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          autoFocus
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: '1px solid var(--color-divider)',
            background: 'transparent',
            color: 'var(--color-primary)',
            fontSize: 22,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ overflowY: 'auto', padding: '0 20px 12px', flex: 1 }}>
          {/* IDENTITY */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2, margin: 0, letterSpacing: '-0.01em' }}>
                {dish.nameTranslated}
              </h2>
              <p
                lang={sourceLanguage}
                style={{
                  fontSize: isLowConfidence ? 22 : 16,
                  fontWeight: isLowConfidence ? 500 : 400,
                  color: isLowConfidence ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                  margin: '4px 0 0',
                  lineHeight: 1.3,
                }}
              >
                {dish.nameOriginal}
              </p>
            </div>
            {unitLabel && (
              <div
                className="num"
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                aria-label={`Price ${unitLabel}`}
              >
                {unitLabel}
              </div>
            )}
          </div>
          {isLowConfidence && (
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '4px 0 0', fontStyle: 'italic' }}>
              Translation uncertain — point at the original above to ask your server.
            </p>
          )}

          {dish.descriptionTranslated && (
            <p style={{ fontSize: 14, marginTop: 10, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
              {dish.descriptionTranslated}
            </p>
          )}
          {dish.culturalContext && (
            <p style={{ fontSize: 13, marginTop: 6, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
              {dish.culturalContext}
            </p>
          )}

          {/* SAFETY */}
          {(dish.allergens.length > 0 || dish.allergenUncertainty.length > 0 || noPriceShown || menuId) && (
            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'var(--color-surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {dish.allergens.length > 0 && <AllergenList allergens={dish.allergens} variant="confirmed" />}
              {dish.allergenUncertainty.length > 0 && <AllergenList allergens={dish.allergenUncertainty} variant="maybe" />}
              {noPriceShown && (
                <div
                  role="note"
                  style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: 13,
                    lineHeight: 1.45,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <path d="M12 17h.01" />
                  </svg>
                  <span>No price was shown on the menu — ask the server before ordering.</span>
                </div>
              )}
              {menuId && (
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  aria-expanded={detailsOpen}
                  aria-controls="dish-details-panel"
                  style={disclosureToggleStyle}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: detailsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  <span>What is this dish?</span>
                </button>
              )}
              {detailsOpen && menuId && (
                <DishDetailsPanel
                  dish={dish}
                  menuId={menuId}
                  targetLanguage={targetLanguage}
                  dietaryReason={dietaryReason}
                />
              )}
            </div>
          )}

          {activeConflict && (
            <div
              role="alert"
              style={{
                marginTop: 12,
                padding: '10px 14px',
                borderRadius: 10,
                background: 'rgba(196,51,43,0.10)',
                border: '1.5px solid rgba(196,51,43,0.40)',
                fontSize: 13,
                lineHeight: 1.45,
                color: 'var(--color-primary)',
              }}
            >
              <strong style={{ color: '#C4332B' }}>Conflict:</strong>{' '}
              This contains {ALLERGEN_NAME[activeConflict.allergen as Allergen]} but you tagged{' '}
              <em style={{ fontStyle: 'normal', fontWeight: 600 }}>{t(`dish.tag.${activeConflict.tag}` as StringKey)} (strict)</em>.
            </div>
          )}

          {/* ADJUST */}
          <button
            type="button"
            onClick={() => setAdjustOpen((v) => !v)}
            aria-expanded={adjustOpen}
            aria-controls="dish-adjust-panel"
            style={adjustToggleStyle}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: adjustOpen ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span>Adjust for this dish</span>
          </button>

          {adjustOpen && (
            <div id="dish-adjust-panel" style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)' }}>{t('dish.dietary')}</label>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{t('dish.dietaryHint')}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    overflowX: 'auto',
                    paddingBottom: 4,
                    marginLeft: -20,
                    marginRight: -20,
                    paddingLeft: 20,
                    paddingRight: 20,
                    scrollbarWidth: 'none',
                  }}
                >
                  {TAG_ORDER.map((tag) => {
                    const sev = tagSeverity.get(tag) ?? null;
                    const isPref = sev === 'preference';
                    const isStrict = sev === 'strict';
                    const accent = isStrict ? '#C4332B' : isPref ? '#B98300' : 'var(--color-divider)';
                    const bg = isStrict ? 'rgba(196,51,43,0.12)' : isPref ? 'rgba(185,131,0,0.12)' : 'transparent';
                    const color = isStrict ? '#C4332B' : isPref ? '#B98300' : 'var(--color-primary)';
                    return (
                      <button
                        key={tag}
                        onClick={() => cycleTag(tag)}
                        aria-pressed={sev !== null}
                        aria-label={`${t(`dish.tag.${tag}` as StringKey)} — ${sev ? (isStrict ? t('dish.severityStrict') : t('dish.severityPreference')) : 'off'}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '8px 12px',
                          borderRadius: 9999,
                          border: `1.5px solid ${accent}`,
                          background: bg,
                          color,
                          fontSize: 13,
                          fontWeight: sev ? 600 : 400,
                          minHeight: 36,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sev !== null && <span aria-hidden style={{ fontSize: 12, fontWeight: 700 }}>?</span>}
                        {t(`dish.tag.${tag}` as StringKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 8 }}>
                  {t('dish.note')}
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {(
                    [
                      { label: t('dish.quickNoOnion'), kind: 'no_onion' as QuickModKind },
                      { label: t('dish.quickLessSpicy'), kind: 'less_spicy' as QuickModKind },
                      { label: t('dish.quickOnSide'), kind: 'on_side' as QuickModKind },
                    ]
                  ).map(({ label, kind }) => (
                    <button
                      key={kind}
                      onClick={() => appendChip(quickModFor(kind, sourceLanguage))}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 9999,
                        border: '1px solid var(--color-divider)',
                        background: 'transparent',
                        color: 'var(--color-primary)',
                        fontSize: 12,
                        minHeight: 32,
                      }}
                    >
                      + {label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={mods}
                  onChange={(e) => setMods(e.target.value)}
                  placeholder={t('dish.notePlaceholder')}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid var(--color-divider)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-primary)',
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div
          style={{
            borderTop: '1px solid var(--color-divider)',
            padding: '10px 20px max(16px, env(safe-area-inset-bottom))',
            background: 'var(--color-bg)',
            flexShrink: 0,
          }}
        >
          {activeState && (
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 12,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.4,
                textAlign: 'center',
              }}
            >
              {activeState}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              role="group"
              aria-label="Quantity"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: 4,
                borderRadius: 9999,
                border: '1px solid var(--color-divider)',
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
                style={footerStepperBtn}
              >
                −
              </button>
              <span
                className="num"
                aria-live="polite"
                style={{ minWidth: 24, textAlign: 'center', fontSize: 16, fontWeight: 600 }}
              >
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                aria-label="Increase quantity"
                style={footerStepperBtn}
              >
                +
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="btn-primary"
              style={{ flex: 1 }}
            >
              {ctaPriceLabel ? `${t('dish.add')} — ${ctaPriceLabel}` : t('dish.add')}
            </button>
          </div>
        </div>

      {contradictionPending && (
        <ConfirmDialog
          open
          destructive
          title={t('dish.contradiction.title').replace('{allergen}', contradictionPending.allergen)}
          body={t('dish.contradiction.body').replace('{tag}', t(`dish.tag.${contradictionPending.tag}` as StringKey))}
          confirmLabel={t('dish.contradiction.override')}
          cancelLabel={t('dish.contradiction.cancel')}
          onCancel={() => setContradictionPending(null)}
          onConfirm={() => {
            setContradictionPending(null);
            submit(true);
          }}
        />
      )}

      {mealModePending && (
        <ConfirmDialog
          open
          destructive
          title={`Mixing ${mealModePending.existing} and ${mealModePending.incoming}`}
          body={`You're building a ${mealModePending.existing} order. This dish is ${mealModePending.incoming}, which kosher rules don't allow in the same meal.`}
          confirmLabel="Override and add"
          cancelLabel={t('dish.contradiction.cancel')}
          onCancel={() => setMealModePending(null)}
          onConfirm={() => {
            setMealModePending(null);
            submit(true);
          }}
        />
      )}

      {reasonPickerOpen && (
        <ReasonPicker
          onPick={(r: DietaryConcernReason) => {
            setDietaryReason(r);
            setReasonPickerOpen(false);
          }}
          onDismiss={() => setReasonPickerOpen(false)}
        />
      )}
    </div>
  );
}

function AllergenList({ allergens, variant }: { allergens: Allergen[]; variant: 'confirmed' | 'maybe' }) {
  const label = variant === 'confirmed' ? 'Contains' : 'May contain';
  const labelColor = variant === 'confirmed' ? '#C4332B' : '#B98300';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: labelColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      {allergens.map((a) => (
        <span key={a} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <AllergenChip allergen={a} variant={variant} />
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-primary)' }}>{ALLERGEN_NAME[a]}</span>
        </span>
      ))}
    </div>
  );
}

// Shared "tap to expand" disclosure toggle style — used by both "What is
// this dish?" (inside the safety strip) and "Adjust for this dish" (below
// the strip). Both are optional depth-on-demand actions; neither should
// compete visually with the primary Add-to-order CTA. Chevron + label,
// no border, no fill.
const disclosureToggleStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 4px',
  background: 'transparent',
  border: 'none',
  color: 'var(--color-primary)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  alignSelf: 'flex-start',
  textAlign: 'left',
};

const adjustToggleStyle: React.CSSProperties = {
  ...disclosureToggleStyle,
  marginTop: 12,
};

// Compact stepper sized to pair inline with the Add CTA in the sticky
// footer. Bigger than the home-icon target (40px) to stay thumb-friendly,
// smaller than a full 44px tap circle so the CTA keeps the visual lead.
const footerStepperBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontSize: 20,
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

// Re-export for callers that don't already import from shared.
export { DIETARY_TAG_LABELS };
